import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Paper,
  IconButton,
  Box,
  Button,
  Switch,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../globals/utils/api";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

function LawFirmList() {
  const navigate = useNavigate();
  const [lawFirms, setLawFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLawFirm, setSelectedLawFirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Fetch law firms from the API
  useEffect(() => {
    const fetchLawFirms = async () => {
      setLoading(true);
      try {
        const response = await apiClient("/list-lawfirms/", "GET");
        setLawFirms(response);
      } catch (error) {
        console.error("Failed to fetch law firms:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLawFirms();
  }, []);

  // Filter law firms based on the search query and active status
  const filteredLawFirms = lawFirms.filter((firm) => {
    const matchesName = firm.name && firm.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesActive = 
      activeFilter === "all" || 
      (activeFilter === "active" && firm.is_active) ||
      (activeFilter === "inactive" && !firm.is_active);
    
    return matchesName && matchesActive;
  });

  useEffect(() => {
    setPage(0);
  }, [searchQuery, activeFilter]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle "Edit" button click
  const handleEditClick = (lawFirm) => {
    navigate(`/lawfirmdetails/${lawFirm.lawfirm_id}`);
  };

  // Handle "New" button click
  const handleNewClick = () => {
    navigate("/add_law_firm");
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (lawFirm) => {
    setSelectedLawFirm(lawFirm);
    setDeleteDialogOpen(true);
  };

  // Confirm delete action
  const handleConfirmDelete = async () => {
    if (!selectedLawFirm) return;
    setDeleteLoading(true);
    try {
      await apiClient(`/lawfirm/${selectedLawFirm.lawfirm_id}`, "DELETE");
      setLawFirms(
        lawFirms.filter(
          (firm) => firm.lawfirm_id !== selectedLawFirm.lawfirm_id
        )
      );
    } catch (error) {
      console.error("Error deleting law firm:", error);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setSelectedLawFirm(null);
    }
  };

  // Handle toggle for is_active
  const handleToggleActive = async (lawFirm) => {
    try {
      const updatedLawFirm = { ...lawFirm, is_active: !lawFirm.is_active };
      await apiClient(`/lawfirm/${lawFirm.lawfirm_id}`, "PATCH", {
        is_active: updatedLawFirm.is_active,
      });

      // Update the local state
      setLawFirms((prevLawFirms) =>
        prevLawFirms.map((firm) =>
          firm.lawfirm_id === lawFirm.lawfirm_id ? updatedLawFirm : firm
        )
      );
    } catch (error) {
      console.error("Error updating law firm:", error);
    }
  };

  // Export to Excel function
  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      const reportData = await apiClient("/lawfirm-report", "GET");
      
      const dataToExport = reportData.map((item) => ({
        "Law Firm Name": item.law_firm_name || "",
        "Location Name": item.location_name || "",
        "Address": item.address || "",
        "User Name": item.user_name || "",
        "Position": item.position || "",
        "Email": item.email || "",
      }));

      exportToExcel(dataToExport, "Law Firm Report", "law_firm_report_export");
    } catch (error) {
      console.error("Failed to export law firms:", error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <Box sx={{ maxWidth: "100%", padding: "20px" }}>
        {/* Heading */}
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          Law Firms
        </Typography>

        {/* Search bar, Active filter, and Action Buttons aligned */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Search by Name"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: "300px",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                label="Status"
                onChange={(e) => setActiveFilter(e.target.value)}
                sx={{
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              px: 1.5,
              py: 0.75,
            }}
          >
            <Button
              sx={{ px: 2, py: 1.5 }}
              variant="outlined"
              color="primary"
              startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={exportToExcel}
              disabled={exportLoading}
            >
              Export
            </Button>

            <Button
              sx={{ px: 2, py: 0.75 }}
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewClick}
            >
              NEW
            </Button>
          </Box>
        </Box>

        {/* Loader or Table */}
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="60vh"
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              overflowX: "auto",
              mb: 2,
              borderRadius: "8px",
            }}
          >
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLawFirms
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((lawFirm) => (
                    <TableRow key={lawFirm.lawfirm_id}>
                      <TableCell>
                        {lawFirm.name ? lawFirm.name : "N/A"}
                      </TableCell>
                      <TableCell>{lawFirm.website}</TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={lawFirm.is_active}
                          onChange={() => handleToggleActive(lawFirm)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEditClick(lawFirm)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(lawFirm)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredLawFirms.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>
              {selectedLawFirm?.name ? selectedLawFirm.name : "this law firm"}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LawFirmList;
